/* atb-quick-find — experience quiz stepper. Reads config + matched products from
   the OS via the App Proxy (/apps/quick-find). 3 questions + 1 optional, inline
   results reveal, answers encoded in the URL for shareability. Progressive
   enhancement + reduced-motion aware. Self-contained (no external widget). */
(function () {
  var REDUCE = false;
  try { REDUCE = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  // Strip markdown emphasis the AI sometimes emits (*your*, **bold**, _x_, `x`)
  // so it never renders literally. Belt-and-suspenders with the backend sanitizer.
  function clean(s) { return String(s == null ? "" : s).replace(/[*_`~]+/g, ""); }
  function money(v) { var n = parseFloat(v); return isNaN(n) ? "" : "$" + n.toFixed(2); }
  function sized(src, w) {
    if (!src) return "";
    var u = src.indexOf("//") === 0 ? "https:" + src : src;
    return u.replace(/(\.(?:jpe?g|png|webp|avif|gif))(\?|$)/i, "_" + w + "x$1$2");
  }
  var liveEl = null;
  function live(msg) {
    if (!liveEl) {
      liveEl = document.createElement("div");
      liveEl.setAttribute("aria-live", "polite");
      liveEl.style.cssText = "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);";
      document.body.appendChild(liveEl);
    }
    liveEl.textContent = msg;
  }

  function init(mount) {
    var slug = mount.getAttribute("data-atb-qf-slug") || "default";
    var proxy = (mount.getAttribute("data-atb-qf-proxy") || "/apps/quick-find").replace(/\/$/, "");
    var tpl = mount.querySelector("[data-atb-qf-card-tpl]");
    var root = mount.querySelector(".atb-qf");
    if (!root || !tpl) return;
    var state = { config: null, step: 0, answers: {} };

    function sid() {
      try {
        var k = "atb_qf_sid", v = sessionStorage.getItem(k);
        if (!v) { v = Date.now().toString(36) + Math.random().toString(36).slice(2, 8); sessionStorage.setItem(k, v); }
        return v;
      } catch (e) { return "anon"; }
    }
    function device() {
      var w = window.innerWidth || screen.width || 0;
      if (w && w < 600) return "mobile";
      if (w && w < 1024) return "tablet";
      return "desktop";
    }
    function beacon(eventType, extra) {
      try {
        fetch(proxy + "/analytics", {
          method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
          body: JSON.stringify(Object.assign({ eventType: eventType, sessionId: sid(), device: device() }, extra || {})),
        });
      } catch (e) {}
    }

    function loadConfig() {
      var ck = "atb_qf_cfg_" + slug;
      try { var c = sessionStorage.getItem(ck); if (c) return Promise.resolve(JSON.parse(c)); } catch (e) {}
      return fetch(proxy + "/config?slug=" + encodeURIComponent(slug), { credentials: "same-origin" })
        .then(function (r) { if (!r.ok) throw new Error("config"); return r.json(); })
        .then(function (cfg) { try { sessionStorage.setItem(ck, JSON.stringify(cfg)); } catch (e) {} return cfg; });
    }

    var params = new URLSearchParams(location.search);
    var deepLink = params.get("answers");
    var a1 = params.get("a1"); // home-teaser handoff: Q1 pre-filled, resume at Q2
    if (deepLink) {
      // Deep-link straight to results: paint the RESULTS skeleton immediately so we
      // never flash the quiz-shaped skeleton first (that was the "two skeletons
      // switch / page jumps" issue). Config is only needed for "Start over", so
      // load it in the background.
      beacon("quiz_view");
      showResults(deepLink.split(",").filter(Boolean));
      loadConfig().then(function (cfg) { state.config = cfg; }).catch(function () {});
    } else if (a1) {
      // Came from the home-page teaser with question 1 already answered — open at
      // question 2 with Q1 pre-filled (if the value is a real Q1 option).
      renderLoadingQuiz();
      loadConfig().then(function (cfg) {
        state.config = cfg;
        beacon("quiz_view");
        var q1 = cfg.questions && cfg.questions[0];
        var valid = q1 && (q1.options || []).some(function (o) { return o.value === a1; });
        if (valid) { state.answers[q1.id] = a1; renderStep(1); }
        else { renderStep(0); }
      }).catch(function () { renderError(); });
    } else {
      renderLoadingQuiz();
      loadConfig().then(function (cfg) {
        state.config = cfg;
        beacon("quiz_view");
        renderStep(0);
      }).catch(function () { renderError(); });
    }

    function ensureConfigThenStart() {
      if (state.config) { renderStep(0); return; }
      renderLoadingQuiz();
      loadConfig().then(function (cfg) { state.config = cfg; renderStep(0); }).catch(function () { renderError(); });
    }

    function renderStep(i) {
      var qs = (state.config && state.config.questions) || [];
      if (i < 0) i = 0;
      if (i >= qs.length) { submit(); return; }
      state.step = i;
      var q = qs[i], total = qs.length;
      var choices = q.options.map(function (o) {
        var sel = state.answers[q.id] === o.value;
        return '<button type="button" class="atb-qf__choice' + (sel ? " is-selected" : "") + '" role="radio" aria-checked="' + (sel ? "true" : "false") + '" data-val="' + esc(o.value) + '">'
          + (o.emoji ? '<span class="atb-qf__choice-emoji">' + esc(o.emoji) + "</span>" : "")
          + '<span class="atb-qf__choice-label">' + esc(o.label) + "</span>"
          + (o.description ? '<span class="atb-qf__choice-sub">' + esc(o.description) + "</span>" : "")
          + "</button>";
      }).join("");
      root.classList.remove("is-loading");
      root.innerHTML =
        '<div class="atb-qf__progress-row"><div class="atb-qf__progress"><span class="atb-qf__progress-fill" style="transform:scaleX(' + Math.max(0.08, (i + 1) / total).toFixed(3) + ')"></span></div><span class="atb-qf__step-count">' + (i + 1) + " / " + total + "</span></div>"
        + '<div class="atb-qf__stage is-entering" tabindex="-1">'
        + '<p class="atb-qf__eyebrow">' + esc(state.config.title || "Quick Find") + "</p>"
        + '<h2 class="atb-qf__head">' + esc(q.prompt) + "</h2>"
        + (q.helpText ? '<p class="atb-qf__help">' + esc(q.helpText) + "</p>" : "")
        + '<div class="atb-qf__choices" data-count="' + q.options.length + '" role="radiogroup" aria-label="' + esc(q.prompt) + '">' + choices + "</div>"
        + '<div class="atb-qf__nav">'
        + (i > 0 ? '<button type="button" class="atb-qf__btn atb-qf__btn--ghost" data-back>Back</button>' : "")
        + (q.optional ? '<button type="button" class="atb-qf__btn atb-qf__btn--ghost" data-skip>Skip</button>' : "")
        + '<button type="button" class="atb-qf__btn atb-qf__btn--primary" data-next' + (state.answers[q.id] ? "" : " disabled") + ">" + (i === total - 1 ? "Find my matches 🔥" : "Continue") + "</button>"
        + "</div></div>";

      var stage = root.querySelector(".atb-qf__stage");
      try { if (stage) stage.focus({ preventScroll: true }); } catch (e) {}

      root.querySelectorAll(".atb-qf__choice").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var v = btn.getAttribute("data-val");
          state.answers[q.id] = v;
          root.querySelectorAll(".atb-qf__choice").forEach(function (b) {
            var on = b === btn;
            b.classList.toggle("is-selected", on);
            b.setAttribute("aria-checked", on ? "true" : "false");
          });
          btn.classList.add("is-confirm");
          beacon("question_answered", { questionId: q.id, answerValue: v });
          var nx = root.querySelector("[data-next]"); if (nx) nx.disabled = false;
          setTimeout(function () { renderStep(i + 1); }, REDUCE ? 0 : 260);
        });
      });
      bind(root, "[data-back]", function () { renderStep(i - 1); });
      bind(root, "[data-skip]", function () { delete state.answers[q.id]; renderStep(i + 1); });
      bind(root, "[data-next]", function () { renderStep(i + 1); });
    }

    function collectValues() {
      var qs = (state.config && state.config.questions) || [], vals = [];
      qs.forEach(function (q) { if (state.answers[q.id]) vals.push(state.answers[q.id]); });
      return vals;
    }

    function submit() {
      var vals = collectValues();
      try { var u = new URL(location.href); u.searchParams.set("answers", vals.join(",")); history.replaceState(null, "", u); } catch (e) {}
      beacon("quiz_complete");
      showResults(vals);
    }

    /* A grid card shows its AI tags as pills, falling back to the generic
       rule-based reasons when the AI copy hasn't been generated yet. On the FIRST
       view of a combo the server generates that copy inline and caches it as it
       goes, so it can return before every card is done — leaving some generic
       pills that a manual refresh would fix. Detect that and self-heal. */
    function aiPillsMissing(recs) {
      for (var i = 1; i < recs.length; i++) { // grid cards = recs after the hero
        if (!(recs[i].aiTags && recs[i].aiTags.length)) return true;
      }
      return false;
    }

    function showResults(vals, attempt) {
      attempt = attempt || 0;
      if (!attempt) renderLoadingResults();
      fetch(proxy + "/results?slug=" + encodeURIComponent(slug) + "&answers=" + encodeURIComponent(vals.join(",")), { credentials: "same-origin" })
        .then(function (r) { if (!r.ok) throw new Error("results"); return r.json(); })
        .then(function (data) {
          renderResults(data, attempt > 0); // retry re-renders in place (no scroll/focus jump)
          if (!attempt) beacon("result_view");
          var recs = (data && data.recommendations) || [];
          // Server copy still warming — silently refetch (max 2x) until the AI
          // pills are cached, so the shopper never has to refresh by hand.
          if (attempt < 2 && recs.length && aiPillsMissing(recs)) {
            setTimeout(function () { showResults(vals, attempt + 1); }, 2600);
          }
        })
        .catch(function () { if (!attempt) renderError(); });
    }

    /* Curve the raw engine score (0-100) into a flattering 88-98% "Match" band.
       Ordering stays honest (higher real score → higher %) but it always reads
       as a win — the validation moment the results page is built around. */
    function matchPct(score) {
      var s = parseFloat(score);
      if (isNaN(s)) s = 70;
      return Math.max(88, Math.min(98, Math.round(88 + s * 0.1)));
    }

    /* Match % per rec, forced strictly DESCENDING by slate position so the badges
       visibly vary (e.g. 98/96/95/93…) instead of all clustering at one number when
       raw scores are close. The hero (index 0) keeps its honest curve and is always
       highest; each later card is at least 1 point lower, floored at 88. */
    function computeMatchPcts(recs) {
      var out = [], prev = 100;
      for (var i = 0; i < recs.length; i++) {
        var p = matchPct(recs[i] && recs[i].score);
        if (p >= prev) p = prev - 1;
        if (p < 88) p = 88;
        out.push(p); prev = p;
      }
      return out;
    }

    function matchScoreHtml(pct) {
      if (pct == null) return "";
      return '<div class="atb-qf__matchscore" role="img" aria-label="' + pct + ' percent match">'
        + '<div class="atb-qf__matchscore-top"><span class="atb-qf__matchscore-num">' + pct + '%</span><span class="atb-qf__matchscore-word">Match</span></div>'
        + '<div class="atb-qf__meter"><span class="atb-qf__meter-fill" style="width:' + pct + '%"></span></div>'
        + "</div>";
    }

    /* Quiz-shaped skeleton (progress + question + choices) for the non-deep-link
       landing. Painted by the JS on boot — there is no server-rendered skeleton,
       so a deep-linked results view never flashes a quiz skeleton first. */
    function renderLoadingQuiz() {
      root.classList.remove("is-loading");
      var choices = "";
      for (var i = 0; i < 4; i++) choices += '<div class="atb-qf__choice atb-qf__choice--skel"><span class="atb-qf__skel"></span></div>';
      root.innerHTML =
        '<div class="atb-qf__progress atb-qf__progress--skel"><span class="atb-qf__progress-fill"></span></div>'
        + '<div class="atb-qf__stage">'
        +   '<span class="atb-qf__skel atb-qf__skel-line atb-qf__skel-line--eyebrow atb-qf__skel-center"></span>'
        +   '<span class="atb-qf__skel atb-qf__skel-line atb-qf__skel-line--head atb-qf__skel-center"></span>'
        +   '<div class="atb-qf__choices">' + choices + "</div>"
        + "</div>";
    }

    /* Loading skeleton shaped like the FINAL results (match head + hero 40/60 +
       3 "you told us" boxes + runner-up grid) so the hydrated layout swaps in
       with no jump and the AI areas read as "loading" not "missing". */
    function renderLoadingResults() {
      root.classList.remove("is-loading");
      function ln(extra) { return '<span class="atb-qf__skel atb-qf__skel-line' + (extra ? " " + extra : "") + '"></span>'; }
      var boxes = "";
      for (var b = 0; b < 3; b++) boxes += '<div class="atb-qf__summary-box">' + ln() + ln() + "</div>";
      var grid = "";
      for (var g = 0; g < 3; g++) grid += '<div class="atb-qf__card"><div class="atb-qf__card-media atb-qf__skel"></div><div class="atb-qf__card-body">' + ln() + ln() + "</div></div>";
      root.innerHTML = '<div class="atb-qf__results">'
        + '<div class="atb-qf__match-head">' + ln("atb-qf__skel-line--eyebrow atb-qf__skel-center") + ln("atb-qf__skel-line--head atb-qf__skel-center")
        + '<div class="atb-qf__matchscore"><div class="atb-qf__meter"><span class="atb-qf__meter-fill" style="width:62%"></span></div></div></div>'
        + '<div class="atb-qf__hero-wrap"><div class="atb-qf__hero"><span class="atb-qf__hero-media atb-qf__skel"></span><div class="atb-qf__hero-detail">' + ln("atb-qf__skel-line--head") + ln() + ln() + ln() + "</div></div></div>"
        + '<section class="atb-qf__told"><div class="atb-qf__summary">' + boxes + "</div></section>"
        + '<section class="atb-qf__more"><div class="atb-qf__grid">' + grid + "</div></section>"
        + "</div>";
    }

    function summaryHtml(summary) {
      if (!summary || !summary.boxes || !summary.boxes.length) return "";
      var boxes = summary.boxes.slice(0, 3).map(function (b) {
        return '<div class="atb-qf__summary-box">'
          + '<span class="atb-qf__summary-emoji" aria-hidden="true">' + esc(b.emoji || "✨") + "</span>"
          + '<p class="atb-qf__summary-label">' + esc(clean(b.label || "")) + "</p>"
          + '<p class="atb-qf__summary-text">' + esc(clean(b.text || "")) + "</p>"
          + "</div>";
      }).join("");
      return '<div class="atb-qf__summary">' + boxes + "</div>";
    }

    /* Conversion-shaped reveal: Match head (headline + score) → Hero (the #1 pick,
       image | recommendation 40/60 with reasons + dual CTA) → "You told us" boxes
       → "More toys" runner-up grid. All AI fields optional; fall back to
       matchReasons / personalizedPitch / title. */
    function renderResults(data, isRetry) {
      var recs = (data && data.recommendations) || [];
      if (!recs.length) { renderEmpty(); return; }
      root.classList.remove("is-loading");
      var summary = (data && data.resultsSummary) || null;
      var headline = (summary && summary.headline) || (data && data.resultsHeadline) || "Look who we matched you with.";
      var hero = recs[0];
      var rest = recs.slice(1);
      var pcts = computeMatchPcts(recs); // pcts[0] = hero, pcts[i] = recs[i] badge

      var wrap = document.createElement("div");
      wrap.className = "atb-qf__results" + (isRetry ? "" : " is-entering"); // no re-animate on silent backfill
      wrap.innerHTML =
        '<div class="atb-qf__match-head" tabindex="-1">'
        +   '<p class="atb-qf__eyebrow">Your match</p>'
        +   '<h2 class="atb-qf__head">' + esc(clean(headline)) + "</h2>"
        +   matchScoreHtml(pcts[0])
        +   '<p class="atb-qf__match-sub">You told us what you’re into. We did the homework — this is the one we’d put money on.</p>'
        + "</div>"
        + '<div class="atb-qf__hero-wrap" data-hero></div>'
        + (summary ? '<section class="atb-qf__told" aria-label="What you told us"><p class="atb-qf__told-eyebrow">You told us</p>' + summaryHtml(summary) + "</section>" : "")
        + (rest.length ? '<section class="atb-qf__more"><h3 class="atb-qf__more-title">More toys you’ll probably get in trouble with</h3><div class="atb-qf__grid" data-grid></div></section>' : "")
        + '<div class="atb-qf__again"><button type="button" class="atb-qf__btn atb-qf__btn--ghost" data-restart>Start over</button><button type="button" class="atb-qf__btn atb-qf__btn--ghost" data-share>Share these picks</button></div>';
      root.innerHTML = "";
      root.appendChild(wrap);

      if (hero) wrap.querySelector("[data-hero]").appendChild(buildHero(hero));
      var gridC = wrap.querySelector("[data-grid]");
      if (gridC) rest.forEach(function (rec, i) { gridC.appendChild(buildMoreCard(rec, pcts[i + 1])); });

      if (!isRetry) {
        // First paint only — a silent AI-backfill retry must not yank focus or scroll.
        try { wrap.querySelector(".atb-qf__match-head").focus({ preventScroll: true }); } catch (e) {}
        try { mount.scrollIntoView({ behavior: REDUCE ? "auto" : "smooth", block: "start" }); } catch (e) {}
        live("Showing " + recs.length + " matches");
      }
      bind(wrap, "[data-restart]", function () { resetUrl(); state.answers = {}; ensureConfigThenStart(); try { mount.scrollIntoView({ behavior: REDUCE ? "auto" : "smooth", block: "start" }); } catch (e) {} });
      bind(wrap, "[data-share]", function () { doShare(wrap.querySelector("[data-share]")); beacon("share"); });
    }

    /* A3 — count a click-through to a product page. Each result card exposes
       several anchors to the same PDP (image, title, "View details"); the click
       navigates away, so fire once per card (guard) with keepalive so the beacon
       survives the page unload. */
    function trackProductClick(node, rec) {
      if (!rec || !rec.shopifyId) return;
      var sent = false;
      node.querySelectorAll('a[href^="/products/"]').forEach(function (a) {
        a.addEventListener("click", function () {
          if (sent) return;
          sent = true;
          beacon("product_click", { productId: rec.shopifyId });
        });
      });
    }

    /* The hero — the #1 match as the page's centrepiece. Image left (40%),
       recommendation right (60%): name, price, sexy one-liner, a "why we picked
       this" ✓-checklist (the answer-derived matchReasons), and dual CTA. The
       primary button reuses the shared addToCart flow (single-variant adds,
       multi-variant opens the picker); "View details" is a plain PDP link. */
    function buildHero(rec) {
      var url = rec.handle ? "/products/" + rec.handle : "#";
      var node = document.createElement("div");
      node.className = "atb-qf__hero";
      var reasons = (rec.matchReasons || []).slice(0, 4);
      var reasonsHtml = reasons.length
        ? '<div class="atb-qf__reasons"><p class="atb-qf__reasons-eyebrow">Why we picked this</p><ul class="atb-qf__reasons-list">'
          + reasons.map(function (r) { return '<li class="atb-qf__reason">' + esc(clean(r)) + "</li>"; }).join("")
          + "</ul></div>"
        : "";
      var desc = clean(rec.sideBody || rec.aiTagline || rec.personalizedPitch || "");
      var imgHtml = rec.imageUrl
        ? '<img class="atb-qf__hero-img" src="' + esc(sized(rec.imageUrl, 800)) + '" alt="' + esc(rec.title) + '" loading="eager" decoding="async">'
        : "";
      node.innerHTML =
        '<a class="atb-qf__hero-media" href="' + esc(url) + '" aria-label="' + esc(rec.title) + '">' + imgHtml + "</a>"
        + '<div class="atb-qf__hero-detail">'
        +   '<a class="atb-qf__hero-name" href="' + esc(url) + '">' + esc(rec.title) + "</a>"
        +   '<span class="atb-qf__hero-price" data-hero-price></span>'
        +   (desc ? '<p class="atb-qf__hero-desc">' + esc(desc) + "</p>" : "")
        +   reasonsHtml
        +   '<div class="atb-qf__hero-cta">'
        +     '<button type="button" class="atb-qf__btn atb-qf__btn--primary atb-qf__hero-atc" data-hero-atc>Add to cart</button>'
        +     '<a class="atb-qf__btn atb-qf__btn--ghost atb-qf__hero-view" href="' + esc(url) + '">View details</a>'
        +   "</div>"
        + "</div>";
      var priceEl = node.querySelector("[data-hero-price]");
      if (priceEl) { if (rec.price) priceEl.textContent = money(rec.price); else if (rec.handle) hydratePrice(rec.handle, priceEl); }
      var atc = node.querySelector("[data-hero-atc]");
      if (atc) atc.addEventListener("click", function () { addToCart(rec, atc); });
      trackProductClick(node, rec);
      return node;
    }

    /* Runner-up card for the "More toys" grid — the full product card plus a
       small corner match-% badge. */
    function buildMoreCard(rec, pct) {
      var node = buildProductCard(rec, false);
      var media = node.querySelector(".atb-qf__card-media");
      if (media) {
        var badge = document.createElement("span");
        badge.className = "atb-qf__match-badge";
        badge.textContent = (pct == null ? matchPct(rec.score) : pct) + "% match";
        media.appendChild(badge);
      }
      return node;
    }

    /* Product card. slim=true (hero/duet) keeps image + title only — the AI
       side-card carries the copy + CTA. slim=false (tail) is the full card. */
    function buildProductCard(rec, slim) {
      var node = tpl.content.firstElementChild.cloneNode(true);
      var url = rec.handle ? "/products/" + rec.handle : "#";
      var link = node.querySelector("[data-atb-qf-link]"), titleLink = node.querySelector("[data-atb-qf-title-link]");
      link.href = url; link.removeAttribute("aria-hidden"); link.removeAttribute("tabindex"); link.setAttribute("aria-label", rec.title);
      titleLink.href = url; titleLink.textContent = rec.title;
      var img = node.querySelector("[data-atb-qf-img]");
      if (rec.imageUrl) { img.src = sized(rec.imageUrl, 600); img.alt = rec.title; } else if (img) { img.remove(); }
      var why = node.querySelector("[data-atb-qf-why]");
      var pitch = node.querySelector("[data-atb-qf-pitch]");
      var price = node.querySelector("[data-atb-qf-price]");
      var atc = node.querySelector("[data-atb-qf-atc]");
      if (slim) {
        // Side-card owns copy + price + CTA — strip them here (no double ATC/fetch).
        [why, pitch, price, atc].forEach(function (el) { if (el) el.remove(); });
      } else {
        var chips = (rec.aiTags && rec.aiTags.length) ? rec.aiTags : (rec.matchReasons || []);
        chips.slice(0, 2).forEach(function (r) { var s = document.createElement("span"); s.className = "atb-qf__why-chip"; s.textContent = clean(r); why.appendChild(s); });
        pitch.textContent = clean(rec.aiTagline || rec.personalizedPitch || "");
        if (rec.price) price.textContent = money(rec.price);
        else if (rec.handle) hydratePrice(rec.handle, price);
        atc.addEventListener("click", function () { addToCart(rec, atc); });
      }
      trackProductClick(node, rec);
      return node;
    }


    /* Add to cart — mirrors favorites/landing: single-variant adds directly,
       multi-variant opens a size/colour picker (never silently adds variant #1).
       On success it drives Dawn's cart-drawer.renderContents() (updates + opens). */
    var pickerModal = null, pickerProduct = null, pickerSelection = {}, pickerRec = null;

    function addToCart(rec, btn) {
      if (!rec.handle) return;
      var original = btn.textContent;
      btn.disabled = true; btn.textContent = "Adding…";
      fetch("/products/" + rec.handle + ".js", { cache: "no-cache" })
        .then(function (r) { return r.json(); })
        .then(function (p) {
          if (!p || !Array.isArray(p.variants) || !p.variants.length) { resetBtn(btn, original, "Unavailable"); return; }
          if (p.variants.length === 1) { performAdd(p.variants[0].id, btn, original, rec); return; }
          btn.disabled = false; btn.textContent = original;
          openPicker(p, rec);
        })
        .catch(function () { btn.textContent = "View"; btn.disabled = false; btn.onclick = function () { location.href = "/products/" + rec.handle; }; });
    }

    function resetBtn(btn, original, msg) { btn.disabled = false; btn.textContent = msg; setTimeout(function () { btn.textContent = original; }, 1800); }

    function performAdd(variantId, btn, original, rec) {
      var cd = document.querySelector("cart-drawer");
      var sectionIds = [];
      if (cd && typeof cd.getSectionsToRender === "function") {
        try { sectionIds = cd.getSectionsToRender().map(function (s) { return s.id; }).filter(Boolean); } catch (e) {}
      }
      var fd = new FormData();
      fd.append("id", String(variantId));
      fd.append("quantity", "1");
      if (sectionIds.length) { fd.append("sections", sectionIds.join(",")); fd.append("sections_url", location.pathname); }
      var routes = (window && window.routes) || {};
      var addUrl = routes.cart_add_url ? routes.cart_add_url + ".js" : "/cart/add.js";
      btn.disabled = true; btn.textContent = "Adding…";
      return fetch(addUrl, { method: "POST", headers: { "Accept": "application/javascript", "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin", body: fd })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (resp) {
          if (!resp.ok || (resp.data && resp.data.status && resp.data.status >= 400)) {
            resetBtn(btn, original, /sold/i.test((resp.data && resp.data.description) || "") ? "Sold out" : "Try again");
            return false;
          }
          beacon("add_to_cart", { productId: rec && rec.shopifyId });
          if (cd && typeof cd.renderContents === "function") {
            if (typeof resp.data.item_count === "number") cd.classList.toggle("is-empty", resp.data.item_count === 0);
            else cd.classList.remove("is-empty");
            cd.renderContents(resp.data); // updates the drawer sections AND opens it
          } else { location.href = "/cart"; return true; }
          btn.textContent = "Added ✓"; btn.classList.add("is-done");
          setTimeout(function () { btn.textContent = original; btn.classList.remove("is-done"); btn.disabled = false; }, 1800);
          return true;
        })
        .catch(function () { resetBtn(btn, original, "Network error"); return false; });
    }

    // iOS-safe body scroll lock (plain overflow:hidden doesn't stop iOS touch scroll)
    function lockBody() { if (document.body.dataset.atbQfScrollY !== undefined) return; var y = window.scrollY || window.pageYOffset || 0; document.body.dataset.atbQfScrollY = String(y); document.body.style.position = "fixed"; document.body.style.top = "-" + y + "px"; document.body.style.left = "0"; document.body.style.right = "0"; document.body.style.width = "100%"; }
    function unlockBody() { var s = document.body.dataset.atbQfScrollY; if (s === undefined) return; var y = parseInt(s, 10) || 0; document.body.style.position = ""; document.body.style.top = ""; document.body.style.left = ""; document.body.style.right = ""; document.body.style.width = ""; delete document.body.dataset.atbQfScrollY; window.scrollTo(0, y); }

    function ensurePicker() {
      if (pickerModal) return pickerModal;
      var host = document.createElement("div");
      host.className = "atb-qf-cart-modal";
      host.setAttribute("hidden", ""); host.setAttribute("role", "dialog"); host.setAttribute("aria-modal", "true");
      host.innerHTML = '<div class="atb-qf-cart-modal__backdrop" data-close></div>'
        + '<div class="atb-qf-cart-modal__content" role="document">'
        + '<button type="button" class="atb-qf-cart-modal__close" aria-label="Close" data-close>&times;</button>'
        + '<h2 class="atb-qf-cart-modal__title"></h2><p class="atb-qf-cart-modal__subtitle"></p>'
        + '<div class="atb-qf-cart-modal__options"></div>'
        + '<div class="atb-qf-cart-modal__footer"><p class="atb-qf-cart-modal__price"></p><button type="button" class="atb-qf-cart-modal__confirm" disabled>Add to cart</button></div>'
        + '</div>';
      document.body.appendChild(host);
      host.querySelectorAll("[data-close]").forEach(function (el) { el.addEventListener("click", closePicker); });
      document.addEventListener("keydown", function (e) { if (e.key === "Escape" && !host.hasAttribute("hidden")) closePicker(); });
      host.querySelector(".atb-qf-cart-modal__confirm").addEventListener("click", function () {
        var btn = this, v = matchVariant();
        if (!v) return;
        Promise.resolve(performAdd(v.id, btn, "Add to cart", pickerRec)).then(function (ok) { if (ok) closePicker(); });
      });
      pickerModal = host;
      return host;
    }

    function openPicker(product, rec) {
      var modal = ensurePicker();
      pickerProduct = product; pickerRec = rec; pickerSelection = {};
      var seed = (product.variants || []).filter(function (v) { return v.available; })[0] || product.variants[0];
      if (seed) (product.options || []).forEach(function (_o, idx) { pickerSelection[idx] = seed["option" + (idx + 1)]; });
      modal.querySelector(".atb-qf-cart-modal__title").textContent = product.title || "Choose options";
      modal.querySelector(".atb-qf-cart-modal__subtitle").textContent = (product.options && product.options.length > 1) ? "Choose your options" : "Choose your option";
      renderPickerOptions(); renderPickerFooter();
      modal.removeAttribute("hidden"); document.body.classList.add("atb-qf-cart-modal-open"); lockBody();
    }
    function closePicker() { if (!pickerModal) return; pickerModal.setAttribute("hidden", ""); document.body.classList.remove("atb-qf-cart-modal-open"); unlockBody(); pickerProduct = null; pickerSelection = {}; pickerRec = null; }

    function renderPickerOptions() {
      if (!pickerProduct || !pickerModal) return;
      var c = pickerModal.querySelector(".atb-qf-cart-modal__options"); c.innerHTML = "";
      (pickerProduct.options || []).forEach(function (option, oi) {
        var name = (typeof option === "string") ? option : (option && option.name) || ("Option " + (oi + 1));
        var row = document.createElement("div"); row.className = "atb-qf-cart-modal__option-row";
        var label = document.createElement("p"); label.className = "atb-qf-cart-modal__option-label"; label.textContent = name; row.appendChild(label);
        var vals = document.createElement("div"); vals.className = "atb-qf-cart-modal__option-values";
        uniqueVals(pickerProduct, oi).forEach(function (value) {
          var b = document.createElement("button"); b.type = "button"; b.className = "atb-qf-cart-modal__option-value"; b.textContent = value;
          if (pickerSelection[oi] === value) b.classList.add("is-selected");
          if (comboSoldOut(pickerProduct, oi, value, pickerSelection)) b.classList.add("is-sold-out");
          b.addEventListener("click", function () { pickerSelection[oi] = value; updatePickerUI(); renderPickerFooter(); });
          vals.appendChild(b);
        });
        row.appendChild(vals); c.appendChild(row);
      });
    }
    function updatePickerUI() {
      if (!pickerProduct || !pickerModal) return;
      pickerModal.querySelectorAll(".atb-qf-cart-modal__option-row").forEach(function (row, oi) {
        row.querySelectorAll(".atb-qf-cart-modal__option-value").forEach(function (b) {
          b.classList.toggle("is-selected", pickerSelection[oi] === b.textContent);
          b.classList.toggle("is-sold-out", comboSoldOut(pickerProduct, oi, b.textContent, pickerSelection));
        });
      });
    }
    function renderPickerFooter() {
      if (!pickerModal || !pickerProduct) return;
      var priceEl = pickerModal.querySelector(".atb-qf-cart-modal__price");
      var confirmEl = pickerModal.querySelector(".atb-qf-cart-modal__confirm");
      var v = matchVariant();
      if (v && v.available) { priceEl.textContent = money((v.price / 100).toFixed(2)); confirmEl.disabled = false; confirmEl.textContent = "Add to cart"; }
      else if (v && !v.available) { priceEl.textContent = money((v.price / 100).toFixed(2)); confirmEl.disabled = true; confirmEl.textContent = "Sold out"; }
      else { priceEl.textContent = ""; confirmEl.disabled = true; confirmEl.textContent = "Add to cart"; }
    }
    function uniqueVals(p, oi) { var seen = {}, out = []; (p.variants || []).forEach(function (v) { var val = v["option" + (oi + 1)]; if (val && !seen[val]) { seen[val] = true; out.push(val); } }); return out; }
    function matchVariant() { if (!pickerProduct) return null; return (pickerProduct.variants || []).filter(function (v) { for (var i = 0; i < (pickerProduct.options || []).length; i++) { if (pickerSelection[i] !== v["option" + (i + 1)]) return false; } return true; })[0] || null; }
    function comboSoldOut(p, oi, value, sel) { var any = false; (p.variants || []).forEach(function (v) { if (v["option" + (oi + 1)] !== value) return; for (var i = 0; i < (p.options || []).length; i++) { if (i === oi) continue; if (sel[i] != null && sel[i] !== v["option" + (i + 1)]) return; } if (v.available) any = true; }); return !any; }

    function hydratePrice(handle, el) {
      fetch("/products/" + handle + ".js", { cache: "no-cache" })
        .then(function (r) { return r.json(); })
        .then(function (p) { if (p && p.price != null) el.textContent = money((p.price / 100).toFixed(2)); })
        .catch(function () {});
    }

    function renderEmpty() {
      root.classList.remove("is-loading");
      root.innerHTML = '<div class="atb-qf__msg"><h3>Nothing matched that combo</h3><p>Let\'s loosen it up — try again, or browse everything.</p><div class="atb-qf__again"><button type="button" class="atb-qf__btn atb-qf__btn--primary" data-restart>Start over</button><a class="atb-qf__btn atb-qf__btn--ghost" href="/collections/all">Browse all</a></div></div>';
      bind(root, "[data-restart]", function () { resetUrl(); state.answers = {}; ensureConfigThenStart(); });
    }
    function renderError() {
      root.classList.remove("is-loading");
      root.innerHTML = '<div class="atb-qf__msg"><h3>Something went sideways</h3><p>We couldn\'t load that right now.</p><div class="atb-qf__again"><button type="button" class="atb-qf__btn atb-qf__btn--primary" data-retry>Try again</button><a class="atb-qf__btn atb-qf__btn--ghost" href="/collections/all">Browse all</a></div></div>';
      bind(root, "[data-retry]", function () { location.reload(); });
    }
    function doShare(btn) {
      var url = location.href;
      if (navigator.share) { navigator.share({ title: document.title, url: url }).catch(function () {}); }
      else if (navigator.clipboard) { navigator.clipboard.writeText(url).then(function () { var o = btn.textContent; btn.textContent = "Link copied ✓"; setTimeout(function () { btn.textContent = o; }, 1500); }); }
    }
    function resetUrl() { try { var u = new URL(location.href); u.searchParams.delete("answers"); history.replaceState(null, "", u); } catch (e) {} }
    function bind(scope, sel, fn) { var el = scope.querySelector(sel); if (el) el.addEventListener("click", fn); }
  }

  function boot() {
    document.querySelectorAll("[data-atb-qf]").forEach(function (m) {
      if (m.dataset.atbQfWired === "1") return;
      m.dataset.atbQfWired = "1";
      try { init(m); } catch (e) { console.warn("[atb-qf]", e); }
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector("[data-atb-qf]") : null;
    if (el && el.dataset.atbQfWired !== "1") { el.dataset.atbQfWired = "1"; try { init(el); } catch (err) {} }
  });
})();
